import React, { useEffect, useRef, useState } from "react";
import { Section } from "../Section/Section";
import { OptionsSearchType } from "@/types/OptionType";
import { getRelativePathFromLink } from "../../utils/url";
import { useRouter } from "next/router";
import styles from "./SearchResult.module.scss";
import { customSanitize } from "../../utils/customSanitize";
import { truncateExcerpt } from "../../utils/truncateExcerpt";
import useUpdateEffect from "../../hooks/useUpdateEffect";
import { Loader } from "../Loader/Loader";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { Date as DateComponent } from "@aleksundshantu/aunds-utils-react/components";
import { LoadMorePagination } from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { convertDateFormat } from "@/utils/convertDateFormat";
import buttonStyles from "../../components/Button/Button.module.scss";
import { Post } from "@/wp/types/client";
import { getDataArrayGeneric } from "@/getData/getData";
import { Button } from "../Button/Button";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";
// blacklisted pages/posts ids
const blacklist = [3, 5140, 5126, 5116, 5132];
interface SearchResultProps {
  searchOptions: OptionsSearchType;
  bgColor: string;
}

export const SearchResult = ({ searchOptions, bgColor }: SearchResultProps) => {
  const { search_page_link } = searchOptions ?? {};
  const { asPath } = useRouter();
  const router = useRouter();
  const { query, locale, defaultLocale } = router;
  const activePath = asPath.match(/^([^?]+)/) || [];
  const link = search_page_link && getRelativePathFromLink(search_page_link);
  const [page, setPage] = useState<number>(
    query.page ? parseInt(query.page as string) : 1,
  );

  const perPage = 12;

  const isSearchPage = activePath[1] === link;

  const [searchResult, setSearchResult] = useState<SearchResultType>({
    keyword: "",
    postsTotal: 0,
    posts: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  // const [prevHeight, setPrevHeight] = useState<string>("auto");
  const [searchPhrase, setSearchPhrase] = useState<string>("");

  const elementsContRef = useRef<HTMLUListElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  const { getLabel, getIcon } = useGlobalContext();

  const LoadSearchData = async (withScroll: boolean) => {
    // if (elementsContRef.current) {
    //   setPrevHeight(`${elementsContRef.current.offsetHeight}px`);
    // }
    setLoading(true);

    const urlParams = new URLSearchParams(window.location.search);
    const keyword = searchPhrase ? searchPhrase : urlParams.get("keyword");

    try {
      const { pageData } = await getDataArrayGeneric<SearchResultType>(
        `/aunds/search/?keyword=${keyword}&page=${page}&per_page=${perPage}`,
        locale,
        defaultLocale,
      );
      setSearchResult({
        keyword: pageData.keyword,
        postsTotal: pageData.postsTotal,
        posts: (pageData.posts || []).filter(
          (post) => !blacklist.includes(post.id),
        ),
      });

      if (pageData.postsTotal > 0) {
        router.push(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              keyword: keyword,
            },
          },
          undefined,
          { shallow: true },
        );
      }

      if (withScroll && mainRef.current) {
        mainRef.current.scrollIntoView({
          behavior: "smooth",
        });
      }
    } catch (error) {
      console.error("Error fetching paginated post data", error);
      setSearchResult({
        keyword: "",
        postsTotal: 0,
        posts: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSearchPage) LoadSearchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useUpdateEffect(() => {
    if (isSearchPage) LoadSearchData(true);
  }, [page]);

  if (!isSearchPage) return null;

  return (
    <Section
      className={`search-result ${styles["search-result"]} ${bgColor}`}
      columnsClassName="is-multiline is-centered"
      disableColumns
      ref={mainRef}
    >
      <div className="columns is-centered">
        <div className="column is-12-mobile is-10-tablet is-8-fullhd">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              LoadSearchData(true);
            }}
          >
            <label htmlFor="search-page-input" className="label">
              {getLabel("search_input_label")}
            </label>
            <input
              id="search-page-input"
              className={styles["search-input"]}
              name="searchbar"
              placeholder={getLabel("search_placeholder")}
              aria-label={getLabel("search_input_aria_label")}
              autoComplete="on"
              autoFocus
              defaultValue={searchPhrase}
              onChange={(e) => {
                setSearchPhrase(e.target.value);
              }}
              // onKeyDown={(e) => {
              //   if (e.key === "Enter") {
              //     LoadSearchData(true);
              //   }
              // }}
            />
            <button
              type="submit"
              aria-label={getLabel("arialabel_search_button").replace(
                "$$keyword",
                searchPhrase,
              )}
              className="button-reset"
            >
              <CustomSVG
                src={getIcon("search")}
                width={32}
                height={32}
                wrapper="span"
                aria-hidden="true"
                className="is-flex"
              />
            </button>
          </form>
        </div>
      </div>
      <ul
        ref={elementsContRef}
        style={{
          minHeight: "20vh",
        }}
        className="columns columns is-multiline is-centered is-relative"
        aria-live="polite"
      >
        {Array.isArray(searchResult.posts) &&
          !loading &&
          searchResult.posts.map((post, index) => {
            const { link } = post;

            const date =
              post.type === "events" ? (
                <DateComponent
                  dateFormat={"dd. MMMM yyyy"}
                  date={
                    new Date(
                      convertDateFormat(post.acf?.header_events?.date) || "",
                    )
                  }
                />
              ) : (
                post.date
              );

            const title =
              post.type === "blog"
                ? post.acf?.header_post?.title || ""
                : post.type === "events"
                  ? post.acf?.header_events?.title || ""
                  : "";

            const excerpt =
              post.type === "blog"
                ? post.acf?.header_post?.excerpt || ""
                : post.type === "events"
                  ? post.acf?.header_events?.excerpt || ""
                  : "";

            return (
              <li
                key={index}
                className={`column is-12-mobile is-10-tablet is-8-fullhd`}
              >
                <article className={`${styles["search-result-item"]} is-box`}>
                  <header>
                    {post.type !== "page" && (
                      <span className="date">{date}</span>
                    )}
                    {title && (
                      <span
                        className={styles["title"]}
                        dangerouslySetInnerHTML={{
                          __html: customSanitize(title),
                        }}
                      />
                    )}
                  </header>
                  {excerpt && (
                    <div
                      className={styles["excerpt"]}
                      dangerouslySetInnerHTML={{
                        __html: customSanitize(truncateExcerpt(excerpt, 256)),
                      }}
                    />
                  )}
                  <footer>
                    <Button
                      className="mt-2"
                      type="secondary"
                      href={link}
                      target="_self"
                    >
                      {getLabel("read_more_label")}
                    </Button>
                  </footer>
                </article>
              </li>
            );
          })}

        {!Array.isArray(searchResult.posts) && !loading && (
          <span className={styles["no-data"]}>
            {getLabel("no_search_results")}
          </span>
        )}

        {loading && <Loader />}

        {searchResult.postsTotal > 0 && (
          <div className="column is-12">
            <LoadMorePagination
              currentPage={page}
              pageType="search"
              postsPerPage={perPage}
              postsTotal={searchResult.postsTotal}
              setPage={setPage}
              setScrollEnabled={() => {}}
              buttonStyles={buttonStyles}
            />
          </div>
        )}
      </ul>
    </Section>
  );
};

type SearchResultType = {
  keyword: string;
  postsTotal: number;
  posts: Post[];
};
